import { createContext, useCallback, useContext } from 'react';
import { useNavigate, useParams } from 'react-router';
import { AppCTX, UserRelations } from 'types';
import { ApplicationContext } from 'providers/ApplicationProvider';
import { baseAction, setDataAction } from 'store/actions';
import { DEFAULT_MIFID_STATUS, EntityType } from 'consts';
import {
	AppParams,
	DecorateUserRelations,
	MifidStatus,
	RelationsURL,
	findSelected,
	getNextPage,
	getPageForStatus
} from 'helpers';

export const AppContext = createContext({} as AppCTX);

export function AppProvider() {
	const ctx = useContext(ApplicationContext);
	const { entityType, flowType } = AppParams(useParams());
	const { config, http, state, dataSelector, dispatch } = ctx;
	const { API, IS_EMPLOYEE_WEB } = config;
	const { context, user } = state;
	const { kennitala } = user;
	const { processInfo } = context;
	const { mifidStatus } = dataSelector(`${API.getMifidStatus}?ssn=${kennitala}`, DEFAULT_MIFID_STATUS);
	const relationsURL = RelationsURL(config, state.user, flowType);
	const navigate = useNavigate();
	const goBack = useCallback(() => navigate(-1), [navigate]);

	const goToNextPage = useCallback(() => {
		const page = getNextPage(processInfo?.processStatus, flowType, entityType);
		page && navigate(`../${page}`);
	}, [processInfo?.processStatus, entityType, flowType, navigate]);

	const Relations = useCallback((): UserRelations => {
		return dataSelector(relationsURL, { children: [], companies: [], person: {} });
	}, [relationsURL, dataSelector]);

	const fetchRelations = async (redirect = false, newEntityType?: EntityType) => {
		const [relations, status] = await Promise.all([
			http.get(relationsURL),
			MifidStatus(kennitala, flowType, API, http)
		]);

		const data = DecorateUserRelations(relations, flowType, status.mifidStatus, IS_EMPLOYEE_WEB);
		const selected = findSelected(data, context.kennitala);
		dispatch([setDataAction, { data: { [relationsURL]: data } }]);

		if (selected) {
			dispatch([baseAction, { context: selected }]);
			if (redirect) {
				const entity = newEntityType ?? entityType;
				const page = getPageForStatus(selected.processInfo?.processStatus, flowType, entity);
				navigate(`../../../${flowType}/${entity}/${page}`);
			}
		}

		return data;
	};

	return {
		...ctx,
		entityType,
		fetchRelations,
		flowType,
		goBack,
		goToNextPage,
		mifidStatus,
		navigate,
		Relations
	};
}
